// sitesApi — typed fetchers for /v1/sites/* (Route 2).
import { apiFetch } from "../../lib/api-client.js";

export type SiteStatus = "active" | "inactive";
export type SiteRisk = "low" | "medium" | "high";

export interface SiteRow {
  id: string;
  tenant_id: string;
  client_id: string;
  client_name: string | null;
  name: string;
  address: string | null;
  timezone: string;
  status: SiteStatus;
  risk_level: SiteRisk;
  location_count: number;
  active_officers: number;
  created_at: string;
  updated_at: string;
}

export interface SiteListResponse {
  items: SiteRow[];
  total: number;
  correlation_id: string | null;
}

export interface SitesQuery {
  tenantId?: string | null;
  q?: string | null;
  status?: SiteStatus | "" | null;
  risk?: SiteRisk | "" | null;
  limit?: number | null;
  offset?: number | null;
}

export class SitesApiError extends Error {
  status: number;
  code: string;
  correlationId: string | null;
  fields?: string[];
  upstreamMessage?: string;
  constructor(status: number, code: string, correlationId: string | null, fields?: string[], upstreamMessage?: string) {
    super(code);
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
    this.fields = fields;
    this.upstreamMessage = upstreamMessage;
  }
}

export function friendlySitesError(err: unknown): string {
  if (err instanceof SitesApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能修改案場資料。";
    if (err.status === 404) return "找不到這個案場（可能已被刪除）。";
    if (err.status === 400) {
      if (err.code === "tenant_id_required")
        return "尚未選定操作的單位，請先到「平台 / 租戶」選擇要進入的單位。";
      if (err.code === "client_not_found") return "選定的客戶不存在或不屬於此單位。";
      if (err.code === "invalid_body") {
        const f = (err.fields ?? []).join("、");
        return `欄位填寫不完整：${f}。`;
      }
    }
    return "資料暫時無法存取，請稍後再試。";
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<SitesApiError> {
  let code = "request_failed";
  let fields: string[] | undefined;
  let cid: string | null = r.headers.get("x-correlation-id");
  let msg: string | undefined;
  try {
    const body = (await r.json()) as { error?: string; correlation_id?: string; fields?: string[]; message?: string };
    code = body.error ?? code;
    cid = body.correlation_id ?? cid;
    fields = body.fields;
    msg = body.message;
  } catch {
    // ignore
  }
  return new SitesApiError(r.status, code, cid, fields, msg);
}

export async function getSite(id: string, tenantId: string | null = null): Promise<SiteRow> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/sites/${encodeURIComponent(id)}${qs}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as SiteRow;
}

export async function listSites(params: SitesQuery = {}): Promise<SiteListResponse> {
  const qs = new URLSearchParams();
  if (params.tenantId) qs.set("tenant_id", params.tenantId);
  if (params.q) qs.set("q", params.q);
  if (params.status) qs.set("status", params.status);
  if (params.risk) qs.set("risk", params.risk);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  const r = await apiFetch(`/v1/sites${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as SiteListResponse;
}

export interface CreateSiteBody {
  client_id: string;
  name: string;
  address?: string | null;
  timezone?: string;
  risk_level?: SiteRisk;
  status?: SiteStatus;
}

export async function createSite(body: CreateSiteBody, tenantId?: string | null): Promise<{ id: string }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/sites${qs}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}

export type PatchSiteBody = Partial<CreateSiteBody>;

export async function patchSite(id: string, body: PatchSiteBody, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/sites/${encodeURIComponent(id)}${qs}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}

export async function archiveSite(id: string, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/sites/${encodeURIComponent(id)}${qs}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}
